import React from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, IconButton, Typography, Box } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SubscriptionResponse } from '@klab-berlin/api-sdk/lib/types/responses/Billing';

import { spaces, palette } from '../../assets/styles/themes/tokens';
import PauseSubscriptionDialogContent from '../ManageSubscriptionDialog/DialogContent/PauseSubDialog';
import { User } from '../../types/User';

// SM-411. The "Mein Abo pausieren" dialog for Stripe subscribers. Mirrors the
// StripeCancelDialog / StripeWithdrawalDialog sibling pattern: a thin dialog
// shell around the shared `PauseSubscriptionDialogContent` (which already
// renders the 1/2/3-month radio picker + support-email footer used by both
// cohorts). The content component branches its submit path on
// `user.isStripeUser` — the Stripe path dispatches `submitPauseRequest` and
// re-fetches subscription-details so the chip flips to "Pause angefordert".
//
// The parent (`SubscriptionInfo`) is responsible for gating this dialog behind
// the SM-411 visibility rule (Amplitude flag on + active + not-trial + no open
// pause / withdrawal / cancel).
export interface StripePauseDialogProps {
  open: boolean;
  onClose: () => void;
  user: User;
  // Optional — a pure Stripe user (no legacy Zuora sub) will pass `undefined`.
  // The Stripe submit path never dereferences this, so `undefined` is safe.
  subscription?: SubscriptionResponse;
}

const StripePauseDialog: React.FC<StripePauseDialogProps> = (props) => {
  const { open, onClose, user, subscription } = props;
  const { t } = useTranslation();

  // The shared content component's `setStep` prop drives the legacy chooser's
  // mini-router (back-to-chooser). There's no chooser above us here, so any
  // navigation from within the content ("Zurück") should just close.
  const setStep = () => onClose();

  return (
    <Dialog onClose={onClose} open={open} fullWidth maxWidth='lg'>
      <DialogTitle
        component='div'
        sx={{
          paddingTop: spaces.lg,
          paddingBottom: spaces.lg,
          borderBottom: `1px solid ${palette.elevation.outlined}`,
          display: 'flex',
          flexDirection: 'column',
          gap: spaces.xs,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant='h5' color={palette.text.primary}>
            {t('Pause my subscription')}
          </Typography>
          <IconButton color='default' size='small' onClick={onClose} data-testid='close-button'>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <PauseSubscriptionDialogContent
        setStep={setStep}
        onClose={onClose}
        user={user}
        subscription={subscription}
      />
    </Dialog>
  );
};

export default StripePauseDialog;
